import React from "react";
import { motion } from "framer-motion";
import { HiHome, HiInformationCircle, HiNewspaper } from "react-icons/hi2";

interface HeaderProps {
  onNavigate: (page: "home" | "about" | "blog") => void;
  currentPage: "home" | "about" | "blog";
}

export const Header: React.FC<HeaderProps> = ({
  onNavigate,
  currentPage,
}) => {
  const navItems = [
    { id: "home" as const, label: "Home", icon: HiHome },
    { id: "about" as const, label: "About", icon: HiInformationCircle },
    { id: "blog" as const, label: "Blog", icon: HiNewspaper },
  ];

  return (
    <motion.header
      className="sticky top-1 z-40 mx-4 sm:mx-6 lg:mx-8"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="relative rounded-2xl border border-white/40 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-2xl bg-white/80">
        {/* Gradient Border Effect */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[var(--brand)] via-[var(--accent)] to-[var(--brand)] opacity-0 hover:opacity-10 transition-opacity duration-300" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo Section */}
            <motion.button
              onClick={() => onNavigate("home")}
              className="flex items-center gap-3 group"
              aria-label="Go to home page"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.div
                className="w-9 h-9 sm:w-11 sm:h-11 bg-gradient-to-br from-[var(--brand)] to-[var(--accent)] rounded-xl flex items-center justify-center shadow-md group-hover:shadow-xl transition-all duration-300"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              >
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  {/* Magnifying glass circle */}
                  <circle cx="10" cy="10" r="7" strokeWidth={2} />
                  {/* Magnifying glass handle */}
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6 6" strokeWidth={2} />
                  {/* AI pixels/dots inside the magnifying glass */}
                  <rect x="7" y="7" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="9" y="7" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="11" y="7" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="6" y="9" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="8" y="9" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="10" y="9" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="12" y="9" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="7" y="11" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="9" y="11" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="11" y="11" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="8" y="13" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="10" y="13" width="1.5" height="1.5" fill="currentColor" />
                </svg>
              </motion.div>
              <span className="text-xl sm:text-2xl font-bold font-serif text-[var(--text-1)] hidden sm:block group-hover:text-[var(--brand)] transition-colors">
                CompareLens
              </span>
            </motion.button>

            {/* Navigation */}
            <nav className="flex items-center gap-1 sm:gap-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentPage === item.id;
                
                return (
                  <motion.button
                    key={item.id}
                    onClick={() => onNavigate(item.id)}
                    className={`relative px-3 sm:px-4 py-2 text-sm sm:text-base font-semibold rounded-lg transition-all duration-200 flex items-center gap-1.5 ${
                      isActive
                        ? "text-[var(--brand)] bg-[var(--brand-lighter)]"
                        : "text-[var(--text-2)] hover:text-[var(--brand)] hover:bg-[var(--surface-3)]"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Icon size={20} />
                    <span className="hidden sm:inline">{item.label}</span>
                    
                    {isActive && (
                      <motion.span
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-[var(--brand)] to-[var(--accent)] rounded-full"
                        layoutId="activeTab"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>
    </motion.header>
  );
};
